import React from 'react'
import { useCurrentFrame, useVideoConfig, interpolate } from 'remotion'
import { COLORS, FONTS, GLOW, GLASS } from '../shared/theme'
import { fadeIn, entrance, staggerDelay, pulse, breathe, SPRINGS } from '../shared/animations'
import SceneWrapper from '../shared/SceneWrapper'

/**
 * DataViz — Visualizacion de datos animada.
 *
 * params: { chart_type, data: [{label, value}], unit }
 */
export default function DataViz({ params = {}, artDirection = {} }) {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const { chart_type = 'bar', data = [], unit = '' } = params
  const texture = artDirection?.texture

  if (chart_type === 'counter')
    return <CounterViz frame={frame} fps={fps} data={data} unit={unit} texture={texture} />
  if (chart_type === 'pie')
    return <PieViz frame={frame} fps={fps} data={data} unit={unit} texture={texture} />
  if (chart_type === 'line')
    return <LineViz frame={frame} fps={fps} data={data} unit={unit} texture={texture} />
  if (chart_type === 'progress_ring')
    return <ProgressRingViz frame={frame} fps={fps} data={data} unit={unit} texture={texture} />
  if (chart_type === 'gauge')
    return <GaugeViz frame={frame} fps={fps} data={data} unit={unit} texture={texture} />
  return <BarViz frame={frame} fps={fps} data={data} unit={unit} texture={texture} />
}

/* ── Bar Chart ──────────────────────────────────── */
function BarViz({ frame, fps, data, unit, texture }) {
  const maxVal = Math.max(...data.map(d => d.value), 1)
  const titleOpacity = fadeIn(frame, 0, 20)
  const glowPulse = pulse(frame, 0.04, 0.6, 1.0)

  const CHART_H = 360
  const gridLines = [0.25, 0.5, 0.75, 1.0]

  return (
    <SceneWrapper accent={COLORS.accent.blue} texture={texture}>
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px 120px',
          gap: 40,
        }}
      >
        <div
          style={{
            opacity: titleOpacity,
            fontFamily: FONTS.heading,
            fontSize: 28,
            color: COLORS.text.muted,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}
        >
          {unit ? `en ${unit}` : 'comparativa'}
        </div>

        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'flex-end',
            gap: 32,
            height: CHART_H + 40,
            width: '100%',
            maxWidth: 1100,
            justifyContent: 'center',
            paddingBottom: 40,
          }}
        >
          {/* Grid lines */}
          {gridLines.map((pct, i) => {
            const y = CHART_H - pct * CHART_H
            const gridOpacity = fadeIn(frame, 5 + i * 3, 15)
            return (
              <div
                key={`grid-${i}`}
                style={{
                  position: 'absolute',
                  top: y,
                  left: 0,
                  right: 0,
                  height: 1,
                  background: `linear-gradient(90deg, transparent, ${COLORS.border}, transparent)`,
                  opacity: gridOpacity * 0.5,
                }}
              />
            )
          })}

          {/* Bars */}
          {data.slice(0, 6).map((item, i) => {
            const delay = staggerDelay(i, 6) + 10
            const progress = entrance(frame, fps, delay, SPRINGS.gentle)
            const barHeight = interpolate(progress, [0, 1], [0, (item.value / maxVal) * CHART_H])
            const opacity = fadeIn(frame, delay, 15)
            const barBreath = breathe(frame, 0.03 + i * 0.005, 0.005)

            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 10,
                  flex: 1,
                  maxWidth: 140,
                  opacity,
                }}
              >
                {/* Value label */}
                <div
                  style={{
                    fontFamily: FONTS.heading,
                    fontSize: 28,
                    fontWeight: 700,
                    color: COLORS.text.primary,
                    transform: `scale(${barBreath})`,
                  }}
                >
                  {item.value}
                  <span style={{ fontSize: 16, color: COLORS.text.muted, marginLeft: 2 }}>
                    {unit}
                  </span>
                </div>
                {/* Bar with gradient + inner highlight */}
                <div
                  style={{
                    width: '100%',
                    height: barHeight,
                    background: `linear-gradient(to top, ${COLORS.accent.blue}CC, ${COLORS.accent.teal})`,
                    borderRadius: '8px 8px 2px 2px',
                    minHeight: 4,
                    boxShadow: `${GLOW.md(COLORS.accent.blue)}, inset 1px 0 0 rgba(255,255,255,0.08)`,
                    opacity: 0.7 + glowPulse * 0.3,
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {/* Inner shine */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '40%',
                      height: '100%',
                      background: 'linear-gradient(90deg, rgba(255,255,255,0.06), transparent)',
                      borderRadius: '8px 0 0 0',
                    }}
                  />
                </div>
                {/* Label */}
                <div
                  style={{
                    fontFamily: FONTS.body,
                    fontSize: 14,
                    color: COLORS.text.secondary,
                    textAlign: 'center',
                    lineHeight: 1.3,
                  }}
                >
                  {item.label}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </SceneWrapper>
  )
}

/* ── Counter with progress ring ──────────────────── */
function CounterViz({ frame, fps, data, unit, texture }) {
  const item = data[0] || { label: '', value: 0 }
  const progress = entrance(frame, fps, 0, SPRINGS.slow)
  const displayValue = Math.round(interpolate(progress, [0, 1], [0, item.value]))
  const opacity = fadeIn(frame, 0, 20)
  const subtitleOpacity = fadeIn(frame, 20, 20)
  const glowPulse = pulse(frame, 0.03, 0.5, 1.0)
  const counterBreath = breathe(frame, 0.02, 0.01)

  // Progress ring
  const ringRadius = 160
  const ringCircumference = 2 * Math.PI * ringRadius
  const ringProgress = interpolate(progress, [0, 1], [ringCircumference, 0])

  return (
    <SceneWrapper accent={COLORS.accent.blue} texture={texture}>
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
        }}
      >
        {/* Ring + number container */}
        <div style={{ position: 'relative', width: 360, height: 360 }}>
          {/* SVG progress ring */}
          <svg width={360} height={360} style={{ position: 'absolute', top: 0, left: 0 }}>
            <defs>
              <filter id="ringGlow" x="-30%" y="-30%" width="160%" height="160%">
                <feGaussianBlur in="SourceGraphic" stdDeviation="6" />
              </filter>
            </defs>
            {/* Background ring */}
            <circle
              cx={180}
              cy={180}
              r={ringRadius}
              fill="none"
              stroke={COLORS.border}
              strokeWidth={3}
            />
            {/* Glow ring */}
            <circle
              cx={180}
              cy={180}
              r={ringRadius}
              fill="none"
              stroke={COLORS.accent.blue}
              strokeWidth={6}
              strokeDasharray={ringCircumference}
              strokeDashoffset={ringProgress}
              strokeLinecap="round"
              transform="rotate(-90, 180, 180)"
              filter="url(#ringGlow)"
              opacity={0.4 * glowPulse}
            />
            {/* Main ring */}
            <circle
              cx={180}
              cy={180}
              r={ringRadius}
              fill="none"
              stroke={COLORS.accent.blue}
              strokeWidth={4}
              strokeDasharray={ringCircumference}
              strokeDashoffset={ringProgress}
              strokeLinecap="round"
              transform="rotate(-90, 180, 180)"
            />
            {/* Endpoint dot */}
            {progress > 0.05 &&
              (() => {
                const angle = -Math.PI / 2 + (1 - ringProgress / ringCircumference) * Math.PI * 2
                const dotX = 180 + Math.cos(angle) * ringRadius
                const dotY = 180 + Math.sin(angle) * ringRadius
                return (
                  <circle
                    cx={dotX}
                    cy={dotY}
                    r={6}
                    fill={COLORS.accent.blue}
                    style={{ filter: `drop-shadow(0 0 6px ${COLORS.accent.blue})` }}
                  />
                )
              })()}
          </svg>

          {/* Number centered in ring */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              opacity,
            }}
          >
            <div
              style={{
                fontFamily: FONTS.heading,
                fontSize: 120,
                fontWeight: 800,
                color: COLORS.text.primary,
                lineHeight: 1,
                letterSpacing: '-0.04em',
                transform: `scale(${counterBreath})`,
                textShadow: `0 0 30px ${COLORS.accent.blue}${Math.round(glowPulse * 25)
                  .toString(16)
                  .padStart(2, '0')}`,
              }}
            >
              {displayValue.toLocaleString()}
              <span style={{ fontSize: 48, color: COLORS.text.secondary }}>{unit}</span>
            </div>
          </div>
        </div>

        <div
          style={{
            opacity: subtitleOpacity,
            fontFamily: FONTS.heading,
            fontSize: 30,
            color: COLORS.text.secondary,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
        >
          {item.label}
        </div>
      </div>
    </SceneWrapper>
  )
}

/* ── Pie chart with real SVG arcs ──────────────────── */
function PieViz({ frame, fps, data, texture }) {
  const titleOpacity = fadeIn(frame, 0, 20)
  const total = data.reduce((s, d) => s + d.value, 0) || 1
  const palette = [
    COLORS.accent.blue,
    COLORS.accent.red,
    COLORS.accent.purple,
    COLORS.accent.green,
    COLORS.accent.orange,
  ]

  const PIE_R = 130
  const PIE_CX = 160
  const PIE_CY = 160
  const circumference = 2 * Math.PI * PIE_R

  // Build arc segments
  let accumulated = 0
  const segments = data.slice(0, 5).map((item, i) => {
    const fraction = item.value / total
    const dashLength = fraction * circumference
    const dashGap = circumference - dashLength
    const rotation = (accumulated / total) * 360 - 90
    accumulated += item.value
    return { item, fraction, dashLength, dashGap, rotation, color: palette[i % palette.length] }
  })

  const pieProgress = entrance(frame, fps, 5, SPRINGS.slow)
  const glowPulse = pulse(frame, 0.04, 0.5, 1.0)

  return (
    <SceneWrapper accent={COLORS.accent.purple} texture={texture}>
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 100,
          padding: '60px 120px',
        }}
      >
        {/* Legend */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
            opacity: titleOpacity,
          }}
        >
          {segments.map((seg, i) => {
            const delay = staggerDelay(i, 6) + 15
            const itemOpacity = fadeIn(frame, delay, 15)
            const pct = Math.round(seg.fraction * 100)
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  opacity: itemOpacity,
                }}
              >
                <div
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: 4,
                    background: seg.color,
                    flexShrink: 0,
                    boxShadow: GLOW.sm(seg.color),
                  }}
                />
                <div
                  style={{
                    fontFamily: FONTS.body,
                    fontSize: 22,
                    color: COLORS.text.secondary,
                    minWidth: 140,
                  }}
                >
                  {seg.item.label}
                </div>
                <div
                  style={{
                    fontFamily: FONTS.heading,
                    fontSize: 28,
                    fontWeight: 700,
                    color: COLORS.text.primary,
                  }}
                >
                  {pct}%
                </div>
              </div>
            )
          })}
        </div>

        {/* SVG Donut chart */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <svg width={320} height={320} style={{ overflow: 'visible' }}>
            <defs>
              <filter id="pieGlow" x="-30%" y="-30%" width="160%" height="160%">
                <feGaussianBlur in="SourceGraphic" stdDeviation="4" />
              </filter>
            </defs>

            {/* Background ring */}
            <circle
              cx={PIE_CX}
              cy={PIE_CY}
              r={PIE_R}
              fill="none"
              stroke={COLORS.card}
              strokeWidth={32}
            />

            {/* Arc segments with animated reveal */}
            {segments.map((seg, i) => {
              const segDelay = staggerDelay(i, 4) + 8
              const segProgress = entrance(frame, fps, segDelay, SPRINGS.gentle)
              const visibleDash = seg.dashLength * segProgress * pieProgress

              return (
                <g key={i}>
                  {/* Glow layer */}
                  <circle
                    cx={PIE_CX}
                    cy={PIE_CY}
                    r={PIE_R}
                    fill="none"
                    stroke={seg.color}
                    strokeWidth={36}
                    strokeDasharray={`${visibleDash} ${circumference - visibleDash}`}
                    transform={`rotate(${seg.rotation}, ${PIE_CX}, ${PIE_CY})`}
                    strokeLinecap="round"
                    filter="url(#pieGlow)"
                    opacity={0.25 * glowPulse}
                  />
                  {/* Main arc */}
                  <circle
                    cx={PIE_CX}
                    cy={PIE_CY}
                    r={PIE_R}
                    fill="none"
                    stroke={seg.color}
                    strokeWidth={28}
                    strokeDasharray={`${visibleDash} ${circumference - visibleDash}`}
                    transform={`rotate(${seg.rotation}, ${PIE_CX}, ${PIE_CY})`}
                    strokeLinecap="round"
                  />
                </g>
              )
            })}

            {/* Center label */}
            <circle cx={PIE_CX} cy={PIE_CY} r={85} fill={COLORS.bg} opacity={0.8} />
            <text
              x={PIE_CX}
              y={PIE_CY - 4}
              textAnchor="middle"
              fill={COLORS.text.primary}
              fontFamily={FONTS.heading}
              fontSize={42}
              fontWeight={800}
              opacity={fadeIn(frame, 15, 20)}
            >
              {total}
            </text>
            <text
              x={PIE_CX}
              y={PIE_CY + 24}
              textAnchor="middle"
              fill={COLORS.text.muted}
              fontFamily={FONTS.body}
              fontSize={14}
              opacity={fadeIn(frame, 20, 15)}
            >
              TOTAL
            </text>
          </svg>
        </div>
      </div>
    </SceneWrapper>
  )
}

/* ── Line Chart ──────────────────────────────── */
function LineViz({ frame, fps, data, unit, texture }) {
  const W = 1000
  const H = 400
  const pad = 60
  const maxVal = Math.max(...data.map(d => d.value), 1)
  const progress = entrance(frame, fps, 5, SPRINGS.slow)
  const titleOpacity = fadeIn(frame, 0, 20)
  const glowPulse = pulse(frame, 0.04, 0.5, 1.0)

  const points = data.map((d, i) => ({
    x: pad + (i / (data.length - 1 || 1)) * (W - pad * 2),
    y: H - pad - (d.value / maxVal) * (H - pad * 2),
  }))

  const totalLen = points.reduce((acc, p, i) => {
    if (i === 0) return 0
    const prev = points[i - 1]
    return acc + Math.hypot(p.x - prev.x, p.y - prev.y)
  }, 0)

  const drawLen = interpolate(progress, [0, 1], [0, totalLen])

  const pathD = points.reduce(
    (acc, p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `${acc} L ${p.x} ${p.y}`),
    ''
  )

  // Grid lines
  const gridValues = [0.25, 0.5, 0.75, 1.0]

  return (
    <SceneWrapper accent={COLORS.accent.blue} texture={texture}>
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 32,
          padding: '60px 120px',
        }}
      >
        <div
          style={{
            opacity: titleOpacity,
            fontFamily: FONTS.heading,
            fontSize: 28,
            color: COLORS.text.muted,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
          }}
        >
          {unit || 'evolucion'}
        </div>
        <svg width={W} height={H} style={{ overflow: 'visible' }}>
          <defs>
            <filter id="lineGlow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="6" />
            </filter>
            <linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={COLORS.accent.blue} stopOpacity="0.18" />
              <stop offset="100%" stopColor={COLORS.accent.blue} stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Horizontal grid lines */}
          {gridValues.map((pct, i) => {
            const y = H - pad - pct * (H - pad * 2)
            const val = Math.round(maxVal * pct)
            return (
              <g key={`g-${i}`} opacity={fadeIn(frame, 3 + i * 2, 12) * 0.3}>
                <line
                  x1={pad}
                  y1={y}
                  x2={W - pad}
                  y2={y}
                  stroke={COLORS.text.dim}
                  strokeWidth={1}
                  strokeDasharray="4 6"
                />
                <text
                  x={pad - 12}
                  y={y + 4}
                  textAnchor="end"
                  fill={COLORS.text.dim}
                  fontFamily={FONTS.mono}
                  fontSize={11}
                >
                  {val}
                </text>
              </g>
            )
          })}

          {/* Area under curve */}
          {points.length > 1 && (
            <path
              d={`${pathD} L ${points[points.length - 1].x} ${H - pad} L ${points[0].x} ${H - pad} Z`}
              fill="url(#areaFill)"
              opacity={progress * 0.6}
            />
          )}

          {/* Glow line */}
          <path
            d={pathD}
            fill="none"
            stroke={COLORS.accent.blue}
            strokeWidth={8}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray={totalLen}
            strokeDashoffset={totalLen - drawLen}
            filter="url(#lineGlow)"
            opacity={0.3 * glowPulse}
          />
          {/* Main line */}
          <path
            d={pathD}
            fill="none"
            stroke={COLORS.accent.blue}
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray={totalLen}
            strokeDashoffset={totalLen - drawLen}
          />
          {/* Points */}
          {points.map((p, i) => {
            const pOpacity = fadeIn(frame, staggerDelay(i, 5) + 10, 10)
            return (
              <g key={i}>
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={14}
                  fill="none"
                  stroke={COLORS.accent.blue}
                  strokeWidth={1}
                  opacity={pOpacity * 0.25}
                />
                <circle cx={p.x} cy={p.y} r={7} fill={COLORS.accent.blue} opacity={pOpacity} />
                <circle cx={p.x} cy={p.y} r={2.5} fill="#fff" opacity={pOpacity * 0.8} />
                <text
                  x={p.x}
                  y={p.y - 22}
                  textAnchor="middle"
                  fill={COLORS.text.primary}
                  fontFamily={FONTS.heading}
                  fontSize={17}
                  fontWeight={700}
                  opacity={pOpacity}
                >
                  {data[i].value}
                  {unit}
                </text>
                <text
                  x={p.x}
                  y={H - 10}
                  textAnchor="middle"
                  fill={COLORS.text.muted}
                  fontFamily={FONTS.body}
                  fontSize={13}
                  opacity={pOpacity}
                >
                  {data[i].label}
                </text>
              </g>
            )
          })}
        </svg>
      </div>
    </SceneWrapper>
  )
}

/* ── Progress Ring (Apple Watch style) ──────────────────────────── */
function ProgressRingViz({ frame, fps, data, unit, texture }) {
  const PALETTE = [COLORS.accent.blue, COLORS.accent.green, COLORS.accent.orange]
  const RADII = [150, 110, 72]
  const STROKE_W = 18
  const CX = 180
  const CY = 180

  const items = data.slice(0, 3)
  const titleOpacity = fadeIn(frame, 0, 18)
  const glowPulse = pulse(frame, 0.04, 0.4, 1.0)

  return (
    <SceneWrapper accent={COLORS.accent.blue} texture={texture}>
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 80,
          padding: '60px 120px',
        }}
      >
        {/* Concentric rings SVG */}
        <svg width={360} height={360} style={{ flexShrink: 0, overflow: 'visible' }}>
          {items.map((item, i) => {
            const r = RADII[i]
            const circumference = 2 * Math.PI * r
            const color = PALETTE[i % PALETTE.length]
            const delay = i * 10
            const ringProgress = entrance(frame, fps, delay, SPRINGS.slow)
            // Treat value as percentage (0-100) by default; clamp to 1
            const fraction = Math.min(item.value / 100, 1)
            const filledLen = fraction * ringProgress * circumference
            const endAngle = -Math.PI / 2 + fraction * ringProgress * 2 * Math.PI
            const dotX = CX + Math.cos(endAngle) * r
            const dotY = CY + Math.sin(endAngle) * r

            return (
              <g key={i} opacity={fadeIn(frame, delay, 12)}>
                {/* Track */}
                <circle
                  cx={CX}
                  cy={CY}
                  r={r}
                  fill="none"
                  stroke={`${color}14`}
                  strokeWidth={STROKE_W}
                />
                {/* Glow */}
                <circle
                  cx={CX}
                  cy={CY}
                  r={r}
                  fill="none"
                  stroke={color}
                  strokeWidth={STROKE_W + 10}
                  strokeDasharray={`${filledLen} ${circumference - filledLen}`}
                  strokeLinecap="round"
                  transform={`rotate(-90, ${CX}, ${CY})`}
                  opacity={0.18 * glowPulse}
                  style={{ filter: `blur(6px)` }}
                />
                {/* Fill */}
                <circle
                  cx={CX}
                  cy={CY}
                  r={r}
                  fill="none"
                  stroke={color}
                  strokeWidth={STROKE_W}
                  strokeDasharray={`${filledLen} ${circumference - filledLen}`}
                  strokeLinecap="round"
                  transform={`rotate(-90, ${CX}, ${CY})`}
                />
                {/* Endpoint dot */}
                {ringProgress > 0.05 && (
                  <>
                    <circle cx={dotX} cy={dotY} r={STROKE_W / 2} fill={color} opacity={glowPulse} />
                    <circle
                      cx={dotX}
                      cy={dotY}
                      r={STROKE_W}
                      fill={color}
                      opacity={0.2 * glowPulse}
                      style={{ filter: `blur(4px)` }}
                    />
                  </>
                )}
              </g>
            )
          })}
        </svg>

        {/* Legend */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
          {items.map((item, i) => {
            const color = PALETTE[i % PALETTE.length]
            const delay = i * 10
            const itemOpacity = fadeIn(frame, delay + 8, 14)
            const displayPct = Math.round(
              interpolate(
                entrance(frame, fps, delay, SPRINGS.slow),
                [0, 1],
                [0, Math.min(item.value, 100)],
                { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
              )
            )
            return (
              <div
                key={i}
                style={{ opacity: itemOpacity, display: 'flex', flexDirection: 'column', gap: 4 }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span
                    style={{
                      fontFamily: FONTS.heading,
                      fontSize: 52,
                      fontWeight: 800,
                      color,
                      letterSpacing: '-0.02em',
                      lineHeight: 1,
                    }}
                  >
                    {displayPct}
                  </span>
                  <span style={{ fontFamily: FONTS.mono, fontSize: 22, color, opacity: 0.7 }}>
                    {unit || '%'}
                  </span>
                </div>
                <div
                  style={{
                    fontFamily: FONTS.body,
                    fontSize: 17,
                    color: COLORS.text.muted,
                    letterSpacing: '0.04em',
                  }}
                >
                  {item.label}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </SceneWrapper>
  )
}

/* ── Gauge / speedometer ──────────────────────────────────────────── */
function GaugeViz({ frame, fps, data, unit, texture }) {
  const item = data[0] || { label: '', value: 0 }
  const maxValue = data.length > 1 ? data[1].value : 100

  const CX = 250
  const CY = 220
  const R = 170
  const STROKE_W = 20
  const circumference = 2 * Math.PI * R
  // 240° arc from 150° to 30° (8 o'clock to 4 o'clock, through 12)
  const GAUGE_DEG = 240
  const trackLen = circumference * (GAUGE_DEG / 360)

  const animProgress = entrance(frame, fps, 0, SPRINGS.slow)
  const fraction = Math.min(Math.abs(item.value) / (maxValue || 100), 1)
  const filledLen = trackLen * fraction * animProgress
  const glowPulse = pulse(frame, 0.04, 0.5, 1.0)

  // Counter
  const displayValue = interpolate(animProgress, [0, 1], [0, item.value], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })
  const formatted = item.value % 1 !== 0 ? displayValue.toFixed(1) : Math.round(displayValue)

  // Needle
  const needleAngleDeg = 150 + fraction * animProgress * GAUGE_DEG
  const needleAngleRad = (needleAngleDeg * Math.PI) / 180
  const needleLen = R - 40
  const needleTipX = CX + needleLen * Math.cos(needleAngleRad)
  const needleTipY = CY + needleLen * Math.sin(needleAngleRad)

  // Tick marks (5 major ticks)
  const TICK_COUNT = 5
  const ticks = Array.from({ length: TICK_COUNT }, (_, i) => {
    const angleDeg = 150 + (i / (TICK_COUNT - 1)) * GAUGE_DEG
    const angleRad = (angleDeg * Math.PI) / 180
    const inner = R - 28
    const outer = R - 8
    return {
      x1: CX + inner * Math.cos(angleRad),
      y1: CY + inner * Math.sin(angleRad),
      x2: CX + outer * Math.cos(angleRad),
      y2: CY + outer * Math.sin(angleRad),
      labelX: CX + (R + 30) * Math.cos(angleRad),
      labelY: CY + (R + 30) * Math.sin(angleRad),
      value: Math.round((i / (TICK_COUNT - 1)) * maxValue),
    }
  })

  const opacity = fadeIn(frame, 0, 20)

  return (
    <SceneWrapper accent={COLORS.accent.blue} texture={texture}>
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 24,
        }}
      >
        <svg
          viewBox="0 0 500 360"
          style={{ width: '68%', maxWidth: 780, overflow: 'visible' }}
          opacity={opacity}
        >
          <defs>
            <filter id="gaugeGlow" x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="6" />
            </filter>
          </defs>

          {/* Track */}
          <circle
            cx={CX}
            cy={CY}
            r={R}
            fill="none"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth={STROKE_W}
            strokeDasharray={`${trackLen} ${circumference - trackLen}`}
            transform={`rotate(150, ${CX}, ${CY})`}
          />

          {/* Colored sections behind fill (gradient segments) */}
          {
            [
              { color: COLORS.accent.green, fraction: 0.33 },
              { color: COLORS.accent.orange, fraction: 0.33 },
              { color: COLORS.accent.red, fraction: 0.34 },
            ].reduce(
              (acc, seg, i) => {
                const start = acc.offset
                const segLen = trackLen * seg.fraction
                acc.offset += segLen
                acc.elements.push(
                  <circle
                    key={i}
                    cx={CX}
                    cy={CY}
                    r={R}
                    fill="none"
                    stroke={seg.color}
                    strokeWidth={STROKE_W}
                    strokeDasharray={`${segLen} ${circumference - segLen}`}
                    strokeDashoffset={-start}
                    transform={`rotate(150, ${CX}, ${CY})`}
                    opacity={0.08}
                  />
                )
                return acc
              },
              { offset: 0, elements: [] }
            ).elements
          }

          {/* Glow fill */}
          <circle
            cx={CX}
            cy={CY}
            r={R}
            fill="none"
            stroke={COLORS.accent.blue}
            strokeWidth={STROKE_W + 14}
            strokeDasharray={`${filledLen} ${circumference - filledLen}`}
            transform={`rotate(150, ${CX}, ${CY})`}
            filter="url(#gaugeGlow)"
            opacity={0.25 * glowPulse}
          />

          {/* Main fill */}
          <circle
            cx={CX}
            cy={CY}
            r={R}
            fill="none"
            stroke={COLORS.accent.blue}
            strokeWidth={STROKE_W}
            strokeDasharray={`${filledLen} ${circumference - filledLen}`}
            strokeLinecap="round"
            transform={`rotate(150, ${CX}, ${CY})`}
          />

          {/* Tick marks */}
          {ticks.map((tick, i) => (
            <g key={i} opacity={fadeIn(frame, 5 + i * 3, 10) * 0.4}>
              <line
                x1={tick.x1}
                y1={tick.y1}
                x2={tick.x2}
                y2={tick.y2}
                stroke={COLORS.text.dim}
                strokeWidth={1.5}
              />
              <text
                x={tick.labelX}
                y={tick.labelY}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={COLORS.text.dim}
                fontFamily={FONTS.mono}
                fontSize={11}
              >
                {tick.value}
              </text>
            </g>
          ))}

          {/* Needle */}
          {animProgress > 0.02 && (
            <>
              <line
                x1={CX}
                y1={CY}
                x2={needleTipX}
                y2={needleTipY}
                stroke={COLORS.text.primary}
                strokeWidth={3}
                strokeLinecap="round"
                opacity={0.9}
              />
              <circle cx={CX} cy={CY} r={10} fill={COLORS.accent.blue} />
              <circle
                cx={CX}
                cy={CY}
                r={16}
                fill={COLORS.accent.blue}
                filter="url(#gaugeGlow)"
                opacity={0.4 * glowPulse}
              />
              <circle cx={CX} cy={CY} r={4} fill="#fff" opacity={0.8} />
            </>
          )}

          {/* Center value */}
          <text
            x={CX}
            y={CY + 55}
            textAnchor="middle"
            fill={COLORS.text.primary}
            fontFamily={FONTS.heading}
            fontSize={56}
            fontWeight={800}
            letterSpacing="-2"
          >
            {formatted}
            {unit && (
              <tspan fontFamily={FONTS.mono} fontSize={24} fill={COLORS.text.secondary}>
                {' '}
                {unit}
              </tspan>
            )}
          </text>
        </svg>

        {/* Label */}
        <div
          style={{
            opacity: fadeIn(frame, 20, 18),
            fontFamily: FONTS.heading,
            fontSize: 26,
            fontWeight: 600,
            color: COLORS.text.secondary,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
        >
          {item.label}
        </div>
      </div>
    </SceneWrapper>
  )
}
